import { Supply, neverSupply } from '@proc7ts/supply';
import { noop } from '@proc7ts/primitives';

/**
 * A key of {@link EventKeeper} method returning its {@link AfterEvent} instance.
 *
 * @category Core
 */
const AfterEvent__symbol = ( /*#__PURE__*/Symbol('after-event'));
/**
 * Checks whether the given value implements an {@link EventKeeper} interface.
 *
 * @category Core
 * @typeParam TEvent - Expected event type.
 * @typeParam TOther - Another type the value may have.
 * @param value - A value to check.
 *
 * @returns `true` if the `value` contains an {@link AfterEvent__symbol} method, or `false` otherwise.
 */
function isEventKeeper(value) {
    return !!value
        && (typeof value === 'object' || typeof value === 'function')
        && typeof value[AfterEvent__symbol] === 'function';
}

/**
 * Converts arbitrary event receiver to generic form.
 *
 * The returned event receiver would never send events to original receiver after event supply is cut off.
 *
 * @category Core
 * @typeParam TEvent - An event type. This is a tuple of event receiver parameter types.
 * @param receiver - An event receiver to convert.
 *
 * @returns Event `receiver` in most generic form.
 */
function eventReceiver(receiver) {
    let supply;
    let receive;
    if (typeof receiver === 'function') {
        supply = new Supply();
        receive = (_context, ...event) => receiver(...event);
    }
    else {
        supply = receiver.supply || new Supply();
        receive = (context, ...event) => {
            if (!supply.isOff) {
                // Supply cut off callback may be called before the receiver disabled.
                // Such callback may send an event that should not be received.
                receiver.receive(context, ...event);
            }
        };
    }
    // Disable receiver when event supply is cut off. But see the comment above.
    // For function receiver this callback is always the first one.
    supply.whenOff(() => receive = EventReceiver$doDonReceive);
    return {
        supply,
        receive: (context, ...event) => receive(context, ...event),
    };
}
function EventReceiver$doDonReceive(_context, ..._event) {
    // Do not receive event.
}

/**
 * Creates an event receiver function that dispatches events to each of the given event receivers.
 *
 * @internal
 * @param receivers - An iterable of event receivers to dispatch event to.
 *
 * @returns An event receiver function that does not utilize event processing context an thus can be called directly.
 */
function receiveByEach(receivers) {
    let send = sendNonRecurrent;
    return (...event) => send(event);
    function sendNonRecurrent(event) {
        let actualReceivers = receivers;
        const received = [];
        send = (recurrent) => received.push(recurrent);
        try {
            for (;;) {
                actualReceivers = processEvent(actualReceivers, event);
                const recurrent = received.shift();
                if (!recurrent) {
                    break;
                }
                event = recurrent;
            }
        }
        finally {
            send = sendNonRecurrent;
        }
    }
}
/**
 * @internal
 */
function processEvent(receivers, event) {
    const recurrentReceivers = [];
    for (const receiver of receivers) {
        const idx = recurrentReceivers.length;
        recurrentReceivers.push(receiver);
        const context = {
            onRecurrent(recurrentReceiver) {
                recurrentReceivers[idx] = eventReceiver({
                    supply: receiver.supply,
                    receive(_context, ...recurrentEvent) {
                        recurrentReceiver(...recurrentEvent);
                    },
                });
            },
        };
        receiver.receive(context, ...event);
    }
    return recurrentReceivers;
}

/**
 * Event notifier can be used to register event receivers and send events to them.
 *
 * It does not implement an {@link EventSender} interface though. Use an {@link EventEmitter} if you need one.
 *
 * Manages a list of registered event receivers, and removes them from the list once their supplies
 * are {@link Supply.off cut off}.
 *
 * @category Core
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 */
class EventNotifier {
    constructor() {
        const rcs = this._rcs = new Set();
        this.send = receiveByEach(rcs);
        this.supply = new Supply(() => {
            rcs.clear();
            delete this._rcs;
        });
    }
    /**
     * The number of currently registered event receivers.
     */
    get size() {
        return this._rcs ? this._rcs.size : 0;
    }
    /**
     * Registers an event receiver.
     *
     * Receivers registered with this method will receive the {@link send emitted} events.
     *
     * @param receiver - A receiver of events to register.
     *
     * @returns A supply of events to the given `receiver`.
     */
    on(receiver) {
        const generic = eventReceiver(receiver);
        const supply = generic.supply.needs(this);
        const receivers = this._rcs;
        if (receivers && !supply.isOff) {
            receivers.add(generic);
            supply.whenOff(() => receivers.delete(generic));
        }
        return supply;
    }
}

/**
 * A key of {@link EventSender} method returning its {@link OnEvent} instance.
 *
 * @category Core
 */
const OnEvent__symbol = ( /*#__PURE__*/Symbol('on-event'));
/**
 * Checks whether the given value implements an {@link EventSender} interface.
 *
 * @category Core
 * @typeParam TEvent - Expected event type.
 * @typeParam TOther - Another type the value may have.
 * @param value - A value to check.
 *
 * @returns `true` if the `value` contains an {@link OnEvent__symbol} method, or `false` otherwise.
 */
function isEventSender(value) {
    return !!value
        && (typeof value === 'object' || typeof value === 'function')
        && typeof value[OnEvent__symbol] === 'function';
}

/**
 * An error indicating a failure to receive an expected event.
 *
 * This happens e.g. when receiver registered in {@link EventKeeper event keeper}, but the latter has no events to send.
 * This may happen when no fallback passed to {@link afterEventBy} function, while the given supplier did not send
 * any events.
 *
 * @category Core
 */
class NoEventsError extends TypeError {
    /**
     * Constructs an error.
     *
     * @param message - Error message.
     */
    constructor(message = 'No events to send') {
        super(message);
    }
}

/**
 * Creates an event sender targeted specific receiver.
 *
 * @category Core
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 *
 * @param receiver - Target event receiver.
 *
 * @returns A function accepting events and sending them to target `receiver`.
 */
function sendEventsTo(receiver) {
    const generic = eventReceiver(receiver);
    let send = receiveByEach([generic]);
    generic.supply.whenOff(() => send = noop);
    return (...event) => send(...event);
}

/**
 * @internal
 */
function AfterEvent$noFallback() {
    throw new NoEventsError();
}

/**
 * @internal
 */
function digEvents(input, extract) {
    return ({ supply, receive }) => {
        let nestedSupply = neverSupply();
        input({
            supply,
            receive: (_context, ...event) => {
                const prevSupply = nestedSupply;
                const extracted = extract(...event);
                nestedSupply = extracted
                    ? extracted({
                        supply: new Supply(reason => {
                            if (reason !== digEvents) {
                                supply.off(reason);
                            }
                        }).needs(supply),
                        receive,
                    })
                    : neverSupply();
                prevSupply.off(digEvents);
            },
        });
    };
}

/**
 * @internal
 */
function OnEvent$do(...processors) {
    return processors.reduce((arg, action) => action(arg), this);
}

/**
 * @internal
 */
function OnEvent$supplier() {
    return this;
}

/**
 * @internal
 */
function onceEvent(supplier) {
    return ({ supply, receive }) => {
        supplier({
            supply,
            receive: (context, ...event) => {
                receive(context, ...event);
                supply.off();
            },
        });
    };
}

/**
 * @internal
 */
function OnEvent$then(onEvent, onCutOff) {
    return new Promise((resolve, reject) => {
        onceEvent(this)({
            supply: onCutOff
                ? new Supply(reason => {
                    try {
                        resolve(onCutOff(reason));
                    }
                    catch (e) {
                        reject(e);
                    }
                })
                : new Supply(reject),
            receive: onEvent
                ? (_ctx, ...event) => {
                    try {
                        resolve(onEvent(...event));
                    }
                    catch (e) {
                        reject(e);
                    }
                }
                : ((_ctx, event) => resolve(event)),
        });
    });
}

/**
 * @internal
 */
function shareEvents(supplier) {
    const sharer = new EventSharer(supplier);
    return sharer.on.bind(sharer);
}
class EventSharer extends EventNotifier {
    constructor(supplier) {
        super();
        this.supplier = supplier;
        this._on = this._onInit();
    }
    on(receiver) {
        this._on.on(receiver);
        return receiver.supply;
    }
    /**
     * Initial dispatcher applied when there are no receivers.
     */
    _onInit() {
        return {
            on: receiver => {
                const initialEvents = [];
                const sharedSupply = new Supply(() => this._on = this._onInit());
                const onFirst = this._on = this._onFirst(sharedSupply, initialEvents);
                try {
                    onFirst.on(receiver);
                    this.supplier({
                        supply: sharedSupply,
                        receive: (_ctx, ...event) => this._on.dispatch(...event),
                    });
                }
                finally {
                    if (this._on === onFirst) {
                        this._on = this._onNext(sharedSupply, initialEvents);
                    }
                }
            },
            dispatch: null, // Initial dispatcher never dispatches events
        };
    }
    /**
     * A dispatcher applied while the first receiver is still registering, but not registered yet.
     *
     * Records emitted events to dispatch them to all receivers.
     */
    _onFirst(sharedSupply, initialEvents) {
        return {
            on: receiver => this._addReceiver(receiver, sharedSupply, initialEvents),
            dispatch: (...event) => {
                // Record initial event.
                initialEvents.push(event);
                this.send(...event);
            },
        };
    }
    /**
     * A dispatcher applied after the first receiver registered.
     *
     * Dispatches initial events to new receivers until new event received.
     */
    _onNext(sharedSupply, initialEvents) {
        return {
            on: receiver => this._addReceiver(receiver, sharedSupply, initialEvents),
            dispatch: (...event) => {
                // An event received after initial ones.
                // Stop dispatching initial events.
                initialEvents.length = 0;
                this.send(...event);
            },
        };
    }
    _addReceiver(receiver, sharedSupply, initialEvents) {
        sharedSupply.cuts(receiver);
        super.on(receiver).whenOff(reason => {
            if (!this.size) {
                sharedSupply.off(reason);
            }
        });
        if (initialEvents.length) {
            // Dispatch initial events.
            const dispatch = sendEventsTo(receiver);
            initialEvents.forEach(event => dispatch(...event));
        }
    }
}

/**
 * @internal
 */
function supplyEvents(supplier, required, dependentSupply) {
    return (receiver) => {
        if (dependentSupply) {
            supplier({
                supply: new Supply().needs(required).cuts(dependentSupply),
                receive: receiver.receive,
            });
        }
        else {
            receiver.supply.needs(required);
            supplier(receiver);
        }
    };
}

/**
 * @internal
 */
function translateEvents(supplier, translate) {
    return receiver => {
        const dispatch = sendEventsTo(receiver);
        supplier({
            supply: receiver.supply,
            receive: (_ctx, ...event) => {
                translate(dispatch, ...event);
            },
        });
    };
}

/**
 * @internal
 */
function alwaysReceiveValue(value) {
    return receiver => {
        try {
            sendEventsTo(receiver)(value);
            receiver.supply.off();
        }
        catch (e) {
            receiver.supply.off(e);
        }
    };
}

/**
 * @internal
 */
function neverReceiveBecause(reason) {
    return ({ supply }) => supply.off(reason);
}
/**
 * @internal
 */
function neverReceive({ supply }) {
    supply.off();
}

export { AfterEvent$noFallback as A, EventNotifier as E, NoEventsError as N, OnEvent__symbol as O, OnEvent$supplier as a, OnEvent$do as b, OnEvent$then as c, AfterEvent__symbol as d, eventReceiver as e, sendEventsTo as f, digEvents as g, isEventSender as h, isEventKeeper as i, alwaysReceiveValue as j, neverReceiveBecause as k, supplyEvents as l, neverReceive as n, onceEvent as o, shareEvents as s, translateEvents as t };
//# sourceMappingURL=_fun-events.base.js.map
